import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Link2,
  ScanSearch,
  Users,
  BrainCircuit,
  Sparkles,
  ShieldCheck,
  CalendarClock,
  Trophy,
} from "lucide-react";
import { Container, Chapter } from "./primitives";

const STEPS = [
  { icon: Link2, k: "Connect Account", d: "OAuth into supported platforms in a single click." },
  { icon: ScanSearch, k: "Scan Conversations", d: "AI listens to public threads matching your ICP." },
  { icon: Users, k: "Discover Leads", d: "Warm profiles surfaced with context intact." },
  { icon: BrainCircuit, k: "AI Analysis", d: "Intent, sentiment, fit and next-best-action." },
  { icon: Sparkles, k: "Generate Engagement", d: "Drafts written in your voice, grounded in the thread." },
  { icon: ShieldCheck, k: "Approve", d: "Team review before anything ships." },
  { icon: CalendarClock, k: "Schedule", d: "Perfect timing, correct time zones, respectfully." },
  { icon: Trophy, k: "Convert Customers", d: "Pipeline movement tracked end-to-end." },
];

export default function Workflow() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  // Horizontal scroll: translate the track based on scroll progress.
  // We move -70% so on mobile it feels punchy but not too fast.
  const x = useTransform(scrollYProgress, [0, 1], ["0%", "-72%"]);
  const progress = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="workflow"
      data-testid="workflow-section"
      ref={ref}
      className="relative border-t border-white/10"
      style={{ height: "320vh" }}
    >
      <div className="sticky top-0 h-screen flex flex-col overflow-hidden">
        <Container className="pt-24 md:pt-28">
          <Chapter
            number="03"
            kicker="The Engine"
            title={
              <>
                From cold thread to closed deal in{" "}
                <span className="font-serif-x italic">eight</span> moves.
              </>
            }
          />
        </Container>

        {/* Progress bar */}
        <div className="relative mt-10 mx-6 md:mx-10 max-w-[1360px] xl:mx-auto w-auto h-[2px] bg-white/8 overflow-hidden rounded-full">
          <motion.div
            style={{ width: progress }}
            className="h-full bg-gradient-to-r from-[#a78bfa] via-[#60a5fa] to-[#22d3ee]"
          />
        </div>

        <div className="flex-1 flex items-center pt-14 pb-24">
          <motion.div
            style={{ x }}
            className="flex gap-6 md:gap-8 pl-6 md:pl-10 pr-[100vw]"
          >
            {STEPS.map((s, i) => (
              <div
                key={s.k}
                className="relative shrink-0 w-[80vw] sm:w-[52vw] md:w-[36vw] lg:w-[26vw] xl:w-[22vw] aspect-[3/4] rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-8 flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono-x text-[11px] tracking-[0.24em] uppercase text-white/40">
                    Step / {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="h-8 w-8 flex items-center justify-center rounded-full border border-white/10 bg-white/[0.04]">
                    <s.icon className="h-4 w-4 text-white/80" strokeWidth={1.5} />
                  </span>
                </div>

                <h4 className="mt-auto font-display text-3xl md:text-4xl tracking-[-0.02em] leading-[1.02] text-white">
                  {s.k}
                </h4>
                <p className="mt-4 text-[15px] text-white/55 leading-relaxed">{s.d}</p>

                <div className="mt-6 flex items-center gap-1.5">
                  {STEPS.map((_, j) => (
                    <span
                      key={j}
                      className={`h-0.5 flex-1 rounded-full ${
                        j <= i ? "bg-white/60" : "bg-white/10"
                      }`}
                    />
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
